import React from 'react';
import type { MarketplaceProduct } from '../marketplaceCatalog';
import { getProductIcon } from './ProductIcons';

export type MarketplaceLogoVariant =
  | 'sidebar'
  | 'catalogGrid'
  | 'catalogListInner'
  | 'pageHeader'
  | 'instanceCard'
  | 'instanceEmpty'
  | 'helpModal';

interface Props {
  product: MarketplaceProduct;
  variant: MarketplaceLogoVariant;
  /** Solo sidebar: estado activo */
  isSelected?: boolean;
}

export const MarketplaceProductLogo: React.FC<Props> = ({ product, variant, isSelected = false }) => {
  const Icon = getProductIcon(product.key);
  const hasLogo = Boolean(product.logoSrc);

  if (hasLogo && product.logoSrc) {
    const img = (
      <img
        src={product.logoSrc}
        alt={product.name}
        loading="lazy"
        decoding="async"
        className={
          variant === 'sidebar'
            ? 'h-full w-full object-contain'
            : variant === 'catalogGrid'
              ? 'max-h-24 w-auto max-w-[90%] object-contain'
              : variant === 'catalogListInner'
                ? 'max-h-20 w-auto max-w-[90%] object-contain'
                : variant === 'pageHeader'
                  ? 'h-9 w-9 object-contain'
                  : 'h-9 w-9 object-contain'
        }
      />
    );

    if (variant === 'sidebar') {
      return (
        <div
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg p-1 ${
            isSelected
              ? 'bg-white ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-600'
              : 'bg-white/95 dark:bg-gray-800/90'
          }`}
        >
          {img}
        </div>
      );
    }

    if (variant === 'catalogGrid') {
      return (
        <div
          className={`flex h-36 items-center justify-center border-b bg-gradient-to-br px-4 ${product.colorScheme.gradientFrom} ${product.colorScheme.gradientTo} ${product.colorScheme.borderColor}`}
        >
            {img}
        </div>
      );
    }

    if (variant === 'catalogListInner') {
      return (
        <div className="flex items-center justify-center rounded-xl ">
          {img}
        </div>
      );
    }

    if (variant === 'pageHeader') {
      return (
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white p-1.5 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-600">
          {img}
        </div>
      );
    }

    if (variant === 'instanceEmpty') {
      return (
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white p-2 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-600">
          <img
            src={product.logoSrc}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="max-h-full max-w-full object-contain opacity-90"
          />
        </div>
      );
    }

    if (variant === 'helpModal') {
      return (
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-white p-2 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-600">
          <img
            src={product.logoSrc}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="max-h-full max-w-full object-contain"
          />
        </div>
      );
    }

    return (
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white p-1 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-600">
        {img}
      </div>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
          isSelected ? product.colorScheme.bg : 'bg-gray-100 dark:bg-gray-700'
        }`}
      >
        <Icon
          className={`h-4 w-4 ${isSelected ? product.colorScheme.text : 'text-gray-400 dark:text-gray-500'}`}
        />
      </div>
    );
  }

  if (variant === 'catalogGrid') {
    return (
      <div
        className={`flex h-36 items-center justify-center border-b bg-gradient-to-br ${product.colorScheme.gradientFrom} ${product.colorScheme.gradientTo} ${product.colorScheme.borderColor}`}
      >
          <Icon className={`h-10 w-10 ${product.colorScheme.text}`} />
      </div>
    );
  }

  if (variant === 'catalogListInner') {
    return (
      <div className={`flex h-16 w-16 items-center justify-center rounded-xl ${product.colorScheme.bg}`}>
        <Icon className={`h-8 w-8 ${product.colorScheme.text}`} />
      </div>
    );
  }

  if (variant === 'pageHeader') {
    return (
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${product.colorScheme.bg}`}>
        <Icon className={`h-5 w-5 ${product.colorScheme.text}`} />
      </div>
    );
  }

  if (variant === 'instanceEmpty') {
    return (
      <div
        className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl ${product.colorScheme.bg}`}
      >
        <Icon className={`h-8 w-8 ${product.colorScheme.text} opacity-50`} />
      </div>
    );
  }

  if (variant === 'helpModal') {
    return (
      <div
        className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg ${product.colorScheme.accent.split(' ')[0]}`}
      >
        <Icon className="h-6 w-6 text-white" />
      </div>
    );
  }

  return (
    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${product.colorScheme.bg}`}>
      <Icon className={`h-5 w-5 ${product.colorScheme.text}`} />
    </div>
  );
};
